import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { batchActions } from 'redux-batched-actions';
import { FetchResult } from 'apollo-boost';
import { Action } from 'redux-actions';
import i18n from 'i18next';

import {
	CheckoutOrderFragment,
	CreateExchangeDocument,
	CreateExchangeMutation,
	CreateExchangeMutationVariables,
	CreateExchangeParams,
	ExchangeOrderFragment,
	RefuseExchangeDocument,
	RefuseExchangeMutation,
	RefuseExchangeMutationVariables
} from '@websky/graphql';
import { ExchangePassengersState, ExchangeSegmentsState } from './types';

import { createClient } from '../../../../graphql-client';
import { Config } from '../../../../context';
import { setLastExchange } from '../../../../Checkout/store/order/actions';
import { getLastExchange } from '../../../../Checkout/store/order/selectors';
import { getGqlErrorMessage } from '@shared/lib/getGqlErrorMessage';

import { getInitialSegmentsState } from '../lib/getInitialSegmentsState';
import { getInitialPassengerState } from '../lib/getInitialPassengerState';
import { shouldSkipPassengers } from '@shared/lib/passengers';

import { setError } from './error/actions';
import { setLoading, stopLoading } from './isLoading/actions';
import { setPassengers } from './passengers/actions';

import { setSegments } from './segments/actions';
import { segmentsSagas } from './segments/sagas';

import { clearExchangeVariants } from './exchangeVariants/actions';
import { getExchangeSelectedFlightsInfo } from './exchangeVariants/selectors';
import { exchangeVariantsSagas } from './exchangeVariants/sagas';

import { CREATE_EXCHANGE_ORDER, CreateExchangePayload, REFUSE_EXCHANGE_ORDER, RefuseExchangePayload } from './actions';
import { getCreateExchangeParams } from './selectors';

function createExchangeMutation(params: CreateExchangeParams) {
	const client = createClient();

	return client.mutate<CreateExchangeMutation, CreateExchangeMutationVariables>({
		mutation: CreateExchangeDocument,
		variables: { params },
		fetchPolicy: 'no-cache'
	});
}

function refuseExchangeMutation(id: string) {
	const client = createClient();

	return client.mutate<RefuseExchangeMutation, RefuseExchangeMutationVariables>({
		mutation: RefuseExchangeDocument,
		variables: { id },
		fetchPolicy: 'no-cache'
	});
}

function* createExchangeOrderSaga({ payload }: Action<CreateExchangePayload>) {
	const createExchangeParams: CreateExchangeParams = yield select(getCreateExchangeParams);
	const exchangeSelectedFlightsInfo: string = yield select(getExchangeSelectedFlightsInfo);

	const mergedCreateExchangeParams = {
		...createExchangeParams,
		...payload.createExchangeParams
	};

	if (exchangeSelectedFlightsInfo) {
		mergedCreateExchangeParams.comment += `\n${exchangeSelectedFlightsInfo}`;
		mergedCreateExchangeParams.comment = mergedCreateExchangeParams.comment.trim();
	}

	yield put(setLoading(payload.isAutoCreate ? 'auto-create-exchange' : 'create-exchange'));

	try {
		const exchangeOrder: FetchResult<CreateExchangeMutation> = yield call(
			createExchangeMutation,
			mergedCreateExchangeParams
		);

		const exchangeOrderData = exchangeOrder?.data?.CreateExchange;

		if (!exchangeOrderData || exchangeOrder.errors?.length) {
			throw new Error(exchangeOrder.errors?.[0]?.message);
		}

		yield put(clearExchangeVariants());
		yield put(setLastExchange(exchangeOrderData));

		if (payload.onSuccess) {
			yield call(payload.onSuccess, exchangeOrderData);
		}
	} catch (err) {
		const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });

		yield put(setError(errorMessage));
	}

	yield put(stopLoading());
}

function* refuseExchangeOrderSaga({ payload }: Action<RefuseExchangePayload>) {
	const lastExchange: ExchangeOrderFragment = yield select(getLastExchange);

	yield put(setLoading('refuse-exchange'));

	try {
		const exchangeOrder: FetchResult<RefuseExchangeMutation> = yield call(refuseExchangeMutation, lastExchange.id);
		const exchangeOrderData = exchangeOrder?.data?.RefuseExare;

		if (!exchangeOrderData || exchangeOrder.errors?.length) {
			throw new Error(exchangeOrder.errors?.[0]?.message);
		}

		yield put(setLastExchange(exchangeOrderData));

		if (payload.onSuccess) {
			yield call(payload.onSuccess, exchangeOrderData);
		}
	} catch (err) {
		const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });

		yield put(setError(errorMessage));
	}

	yield put(stopLoading());
}

export function* createInitialExchangeState({ payload }: Action<CheckoutOrderFragment>) {
	const {
		global: {
			companyInfo: { iataCode }
		}
	}: Config = yield getContext('config');

	const initialValue = shouldSkipPassengers(payload.travellers?.length ?? 0, iataCode);

	const actions: Action<ExchangeSegmentsState | ExchangePassengersState>[] = [
		setSegments(getInitialSegmentsState(payload.flight.segmentGroups)),
		setPassengers(getInitialPassengerState(payload.travellers, initialValue))
	];

	yield put(batchActions(actions));
}

export default function* rootSaga() {
	yield all([
		segmentsSagas(),
		exchangeVariantsSagas(),
		takeLatest(CREATE_EXCHANGE_ORDER, createExchangeOrderSaga),
		takeLatest(REFUSE_EXCHANGE_ORDER, refuseExchangeOrderSaga)
	]);
}
